import { DROPDOWN_FETCH_PAGE_SIZE, usePermissions } from '@klareospace/pharos-lib-front-utils';
import { TEMPLATE_CONFIGURATION_STATUS } from '@klareospace/pharos-lib-types';
import type { TemplateConfigurationWithResolvedRefs } from '@klareospace/pharos-lib-types';
import { deduplicate } from '@klareospace/pharos-lib-utils';

import { useShipperConfigurations } from '&/features/settings/shippers';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ShipperOptionsStatus = 'error' | 'forbidden' | 'loading' | 'none' | 'ready';

/** One selectable entry in the General information Shipper dropdown. */
export interface ShipperOption {
  code: string;
  label: string;
}

export interface UseShipperOptionsReturn {
  /** Distinct, non-empty `shipperCode`s among the active configurations, each labelled with the shipper's resolved name. */
  options: ShipperOption[];
  status: ShipperOptionsStatus;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Resolves the tenant's active shipper configurations backing the manual order drawer's General
 * information Shipper dropdown — one option ⇒ the section pre-selects and locks it; several ⇒ the
 * user must pick one; zero/forbidden/error ⇒ submission stays blocked with an explanatory message.
 * Listing shippers requires `shippers:list` — the query is skipped entirely (not just left to
 * error) while that permission is missing, and is only enabled while the drawer is `open`. `data`
 * is checked before `isError` so a failed background refetch never discards an already-resolved,
 * cached options list.
 */
export function useShipperOptions(open: boolean): UseShipperOptionsReturn {
  const { hasPermission } = usePermissions();
  const canListShippers = hasPermission('shippers:list');

  const { data, isError } = useShipperConfigurations({
    enabled: canListShippers && open,
    page: 1,
    pageSize: DROPDOWN_FETCH_PAGE_SIZE,
    statuses: [TEMPLATE_CONFIGURATION_STATUS.ACTIVE],
  });

  if (!canListShippers) return { options: [], status: 'forbidden' };
  if (data) {
    const options = resolveShipperOptions(data.items);
    return { options, status: options.length === 0 ? 'none' : 'ready' };
  }
  if (isError) return { options: [], status: 'error' };
  return { options: [], status: 'loading' };
}

// ─── Helpers (private) ──────────────────────────────────────────────────────────

/** Distinct, non-empty `shipperCode`s among the active configurations, labelled with the shipper's resolved name (falling back to the code). */
function resolveShipperOptions(configurations: TemplateConfigurationWithResolvedRefs[]): ShipperOption[] {
  const active = configurations.filter(
    (configuration) => configuration.status === TEMPLATE_CONFIGURATION_STATUS.ACTIVE,
  );
  const codes = deduplicate(
    active.map((configuration) => configuration.shipperCode).filter((code): code is string => !!code),
  );
  return codes.map((code) => ({ code, label: resolveShipperLabel(active, code) }));
}

/** The first active configuration's resolved template name for `code`, falling back to the code itself. */
function resolveShipperLabel(configurations: TemplateConfigurationWithResolvedRefs[], code: string): string {
  const match = configurations.find((configuration) => configuration.shipperCode === code);
  return match?.resolvedTemplate?.name ?? code;
}
